<template>
  <div class="wallet-details-container">
    <!-- Breadcrumbs and Header -->
    <div class="header-section">
      <div class="breadcrumbs">
        <a-breadcrumb>
          <a-breadcrumb-item>
            <a @click="$router.push('/financing-and-payments')"
              >Financing & Payments</a
            >
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <a @click="$router.push('/financing-and-payments?tab=wallets')"
              >Wallets Dashboard</a
            >
          </a-breadcrumb-item>
          <a-breadcrumb-item
            >Master Wallets</a-breadcrumb-item
          >
        </a-breadcrumb>
      </div>

      <div class="header-content">
        <div class="title-section">
          <h1>Master Wallets</h1>
        </div>
        <a-button
          type="primary"
          @click="$router.push('/financing-and-payments?tab=wallets')"
          style="background: #26a246; border-color: #26a246"
        >
          Back to Dashboard
        </a-button>
      </div>
    </div>

    <div class="main-content">
      <!-- Left Sidebar -->
      <div class="sidebar">
        <!-- Master Wallet Balance -->
        <div class="wallet-balances">
          <h3>Disbursement Wallet</h3>
          <div class="wallet-balance-item">
            <div class="wallet-balance">
              <span class="balance-amount">
                {{ walletData.disbursementBalance }}
              </span>
              <span class="wallet-code">Disbursement Balance</span>
            </div>
          </div>
        </div>

        <!-- Agent Wallet Balance -->
        <div class="wallet-balances">
          <h3>Collection Wallet</h3>
          <div class="wallet-balance-item">
            <div class="wallet-balance">
              <span class="balance-amount"
                >{{ walletData.collectionBalance }}
              </span>
              <span class="wallet-code">Collection Balance</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel -->
      <div class="content-panel">
        <!-- Tabs -->
        <a-tabs v-model="activeTab" class="green-tabs" :animated="false">
          <a-tab-pane key="transactions" tab="Disbursement History">
            <!-- Filters -->
            <div class="filters-section">
              <div class="filter-group">
                <span class="filter-label">Filter by:</span>
                <a-range-picker
                  v-model="filters.disbursementDateRange"
                  placeholder="['Start Date', 'End Date']"
                  style="width: 250px; margin-right: 16px"
                  format="YYYY-MM-DD"
                  :allowClear="true"
                />
              </div>
            </div>

            <!-- Disbursement Transactions Table -->
            <a-table
              :columns="disbursementColumns"
              :data-source="disbursementTransactions"
              :pagination="disbursementPagination"
              :loading="disbursementLoading"
              row-key="id"
              :scroll="{ y: 400 }"
              @change="handleDisbursementTableChange"
            >
              <template slot="amount" slot-scope="text, record">
                <span
                  :style="{
                    color: record.isCredit === 1 ? '#10b981' : '#ef4444',
                  }"
                >
                  {{ record.isCredit === 1 ? "+ " : "- "
                  }}{{ formatAmount(text) }}
                </span>
              </template>
            </a-table>
          </a-tab-pane>

          <a-tab-pane key="linked-accounts" tab="Collection History">
            <!-- Filter Section -->
            <div class="filters-section">
              <div class="filter-group">
                <span class="filter-label">Filter by:</span>
                <a-range-picker
                  v-model="filters.collectionDateRange"
                  placeholder="['Start Date', 'End Date']"
                  style="width: 250px; margin-right: 16px"
                  format="YYYY-MM-DD"
                  :allowClear="true"
                />
              </div>
            </div>

            <!-- Collection Transactions Table -->
            <a-table
              :columns="collectionColumns"
              :data-source="collectionTransactions"
              :pagination="collectionPagination"
              :loading="collectionLoading"
              row-key="id"
              :scroll="{ y: 400 }"
              @change="handleCollectionTableChange"
            >
              <template slot="amount" slot-scope="text, record">
                <span
                  :style="{
                    color: record.isCredit === 1 ? '#10b981' : '#ef4444',
                  }"
                >
                  {{ record.isCredit === 1 ? "+ " : "- "
                  }}{{ formatAmount(text) }}
                </span>
              </template>
            </a-table>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
// import StatusTag from "../shared-components/StatusTag.vue";
import { getInitials, formatAmount, formatDate } from "@/utils";
import FinancingAndPaymentsService from "../../api/financing-and-payments.service";

export default {
  name: "MasterWalletDetails",
  components: {
    // StatusTag,
  },
  data() {
    return {
      activeTab: "transactions",
      loading: false,
      filters: {
        disbursementDateRange: null,
        collectionDateRange: null,
      },
      disbursementPagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} disbursement transactions`,
        showSizeChanger: false,
      },
      collectionPagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} collection transactions`,
        showSizeChanger: false,
      },
      walletData: {
        disbursementBalance: "0.00",
        collectionBalance: "0.00",
      },
      disbursementTransactions: [],
      disbursementLoading: false,
      collectionTransactions: [],
      collectionLoading: false,
      disbursementColumns: [
        {
          title: "Date",
          dataIndex: "createdAt",
          key: "createdAt",
          customRender: (text) => moment(text).format("MMM DD, YYYY"),
        },
        {
          title: "Type",
          dataIndex: "isCredit",
          key: "type",
          customRender: (text) => (text === 1 ? "Credit" : "Debit"),
        },
        {
          title: "Method",
          dataIndex: "methodType",
          key: "methodType",
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
        },
        {
          title: "Final Balance",
          dataIndex: "finalBalance",
          key: "finalBalance",
          customRender: (text) => formatAmount(text),
        },
        {
          title: "Note",
          dataIndex: "note",
          key: "note",
        },
      ],
      collectionColumns: [
        {
          title: "Date",
          dataIndex: "createdAt",
          key: "createdAt",
          customRender: (text) => moment(text).format("MMM DD, YYYY"),
        },
        {
          title: "Type",
          dataIndex: "isCredit",
          key: "type",
          customRender: (text) => (text === 1 ? "Credit" : "Debit"),
        },
        {
          title: "Method",
          dataIndex: "methodType",
          key: "methodType",
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
        },
        {
          title: "Final Balance",
          dataIndex: "finalBalance",
          key: "finalBalance",
          customRender: (text) => formatAmount(text),
        },
        {
          title: "Note",
          dataIndex: "note",
          key: "note",
        },
      ],
    };
  },
  methods: {
    getInitials,
    formatAmount,
    formatDate,
    async loadWalletData() {
      this.loading = true;
      
      const response = await FinancingAndPaymentsService.getWalletStats();

      if (response.data?.code === 200) {
        const data = response.data.data;

        this.walletData = {
          disbursementBalance: formatAmount(data.disbursementBalance || 0),
          collectionBalance: formatAmount(data.collectionBalance || 0),
        };

        console.log("Master wallet data loaded:", this.walletData);
      } else {
        this.$message.warning("Failed to fetch master wallet details");
      }
      
      this.loading = false;
    },

      async fetchDisbursementTransactions() {
        this.disbursementLoading = true;
        
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.disbursementDateRange && this.filters.disbursementDateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(
            this.filters.disbursementDateRange[0].format("YYYY-MM-DD")
          );
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(
            this.filters.disbursementDateRange[1].format("YYYY-MM-DD")
          );
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          startDate: startDate,
          endDate: endDate,
          pageIndex: this.disbursementPagination.pageIndex,
          pageSize: this.disbursementPagination.pageSize,
        };

        const response = await FinancingAndPaymentsService.getAgentWalletTransactions(params);

        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          console.log("Disbursement transactions response:", data);
          this.disbursementTransactions = data.results || [];
          this.disbursementPagination.total = data.totalCount || 0;
        } else {
          this.disbursementTransactions = [];
          this.disbursementPagination.total = 0;
          this.$message.warning(response.data.message || "Error fetching disbursement transactions");
        }
        
        this.disbursementLoading = false;
      },

      async fetchCollectionTransactions() {
        this.collectionLoading = true;
        
        // Convert dateRange to startDate and endDate
        let startDate = "";
        let endDate = "";

        if (this.filters.collectionDateRange && this.filters.collectionDateRange.length === 2) {
          // Create Date objects with time 00:00:00 for start date
          const startDateObj = new Date(
            this.filters.collectionDateRange[0].format("YYYY-MM-DD")
          );
          startDateObj.setHours(0, 0, 0, 0);
          startDate = startDateObj;

          // Create Date objects with time 23:59:59 for end date
          const endDateObj = new Date(
            this.filters.collectionDateRange[1].format("YYYY-MM-DD")
          );
          endDateObj.setHours(23, 59, 59, 999);
          endDate = endDateObj;
        }

        const params = {
          startDate: startDate,
          endDate: endDate,
          pageIndex: this.collectionPagination.pageIndex,
          pageSize: this.collectionPagination.pageSize,
        };

        const response = await FinancingAndPaymentsService.getCollectionsWalletTransactions(params);

        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          console.log("Collection transactions response:", data);
          this.collectionTransactions = data.results || [];
          this.collectionPagination.total = data.totalCount || 0;
        } else {
          this.collectionTransactions = [];
          this.collectionPagination.total = 0;
          this.$message.warning(response.data.message || "Error fetching collection transactions");
        }
        
        this.collectionLoading = false;
      },

      handleDisbursementTableChange(pagination) {
        if (pagination.current !== this.disbursementPagination.pageIndex) {
          this.disbursementPagination.pageIndex = pagination.current;
          this.fetchDisbursementTransactions();
        }
      },

      handleCollectionTableChange(pagination) {
        if (pagination.current !== this.collectionPagination.pageIndex) {
          this.collectionPagination.pageIndex = pagination.current;
          this.fetchCollectionTransactions();
        }
      },
    },
    watch: {
      "filters.disbursementDateRange"() {
        this.disbursementPagination.pageIndex = 1;
        this.fetchDisbursementTransactions();
      },
      "filters.collectionDateRange"() {
        this.collectionPagination.pageIndex = 1;
        this.fetchCollectionTransactions();
      },
    },
    created() {
      this.loadWalletData();
      this.fetchDisbursementTransactions();
      this.fetchCollectionTransactions();
    },
  mounted() {
    this.$nextTick(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  },
};
</script>

<style scoped>
.wallet-details-container {
  padding: 24px;
  background: #f5f5f5;
}

.header-section {
  margin-bottom: 24px;
}

.breadcrumbs {
  margin-bottom: 16px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.wallet-id {
  margin: 4px 0 0 0;
  color: #6b7280;
  font-size: 14px;
}

.main-content {
  display: flex;
  gap: 24px;
  min-width: 0;
  overflow: hidden;
}

.sidebar {
  width: 320px;
  flex-shrink: 0;
}

.content-panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.user-profile {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  text-align: center;
  border: 1px solid #e5e7eb;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #26a246;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  margin: 0 auto 16px;
}

.user-profile h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.user-id {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.wallet-balances {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.wallet-balances h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wallet-balance-item {
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
}

.wallet-balance-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #26a246;
}

.wallet-balance-item:last-child {
  margin-bottom: 0;
}

.wallet-balance-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #26a246, #10b981, #059669);
}

.wallet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.wallet-name {
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
  text-transform: capitalize;
  letter-spacing: 0.025em;
}

.default-badge {
  background: linear-gradient(135deg, #26a246, #10b981);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 2px 4px rgba(38, 162, 70, 0.3);
}

.wallet-balance {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  border: 1px solid rgba(38, 162, 70, 0.15);
}

.balance-amount {
  font-weight: 800;
  color: #1f2937;
  font-size: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  text-align: center;
  letter-spacing: 0.025em;
}

.wallet-code {
  color: #6b7280;
  font-size: 12px;
  font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas,
    "Courier New", monospace;
  background: rgba(107, 114, 128, 0.08);
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 500;
  text-align: center;
  border: 1px solid rgba(107, 114, 128, 0.1);
}

.filters-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-label {
  color: #6b7280;
  font-size: 14px;
  margin-right: 8px;
}

.empty-state {
  padding: 48px;
  text-align: center;
}

/* Green tabs styling */
.green-tabs >>> .ant-tabs-tab-active {
  color: #26a246 !important;
}

.green-tabs >>> .ant-tabs-ink-bar {
  background-color: #26a246 !important;
}

/* Linked Accounts Table */
.linked-accounts-table {
  width: 100%;
}

.linked-accounts-table >>> .ant-table {
  width: 100%;
}

.linked-accounts-table >>> .ant-table-tbody > tr > td {
  padding: 16px 12px;
}

.linked-accounts-table >>> .ant-table-thead > tr > th {
  padding: 16px 12px;
}

.linked-accounts-table >>> .ant-pagination {
  text-align: center;
  justify-content: center;
  margin-top: 24px;
}

/* Overdraft styling */
.overdraft-section {
  margin-bottom: 16px;
}

.overdraft-item {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.overdraft-item::before {
  background: linear-gradient(90deg, #f59e0b, #d97706, #b45309);
}

.overdraft-status {
  margin-top: 8px;
  text-align: center;
}

.available-amount {
  font-size: 12px;
  color: #92400e;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group > * {
    width: 100%;
  }
}
</style>
